import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiClose, mdiPlus } from '@mdi/js'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { AppIcon } from '../ui/icon'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from '../ApiSearchableSelect'
import { usePropertyDefinitionSearch } from '../../hooks/usePropertyDefinitionSearch'
import { AssetTypeTile, type AssetTypeLike } from '../assets/AssetTypeTile'
import { INPUT_TODAY, childrenInputPath, parentInputPath, sortInputs } from './inputs'

const SELF = '__self__'
// A select value reading the descendants of a type, not an ancestor.
const CHILDREN = 'children:'

interface DerivationInputsFieldProps {
  org: string
  value: string[]
  onChange: (inputs: string[]) => void
  assetTypes: any[]
  readOnly?: boolean
}

// The one input list of a script derivation (spec 2026-10-06-13): property
// codes of the asset, parent.<type>.<code> for the nearest ancestor of a
// type, children.<type>.<code> for the descendants of a type (spec
// 2026-10-06-17), and `today`. The sandbox exposes nothing else.
export function DerivationInputsField({ org, value, onChange, assetTypes, readOnly }: DerivationInputsFieldProps) {
  const { t } = useTranslation('derivations')
  const { searchPropertyDefinitions, initialOptions } = usePropertyDefinitionSearch(org)
  const [picked, setPicked] = useState<{ uid: string; code: string }>({ uid: '', code: '' })
  const pickedCode = picked.code
  const [ancestorType, setAncestorType] = useState(SELF)

  const readsToday = value.includes(INPUT_TODAY)
  const chips = useMemo(() => value.filter((v) => v !== INPUT_TODAY), [value])

  const add = useCallback(() => {
    if (!pickedCode) return
    const path = ancestorType === SELF
      ? pickedCode
      : ancestorType.startsWith(CHILDREN)
        ? childrenInputPath(ancestorType.slice(CHILDREN.length), pickedCode)
        : parentInputPath(ancestorType, pickedCode)
    if (!value.includes(path)) onChange(sortInputs([...value, path]))
    setPicked({ uid: '', code: '' })
  }, [pickedCode, ancestorType, value, onChange])

  const setToday = useCallback((on: boolean) => {
    const others = value.filter((v) => v !== INPUT_TODAY)
    onChange(sortInputs(on ? [...others, INPUT_TODAY] : others))
  }, [value, onChange])

  return (
    <div data-testid="derivation-inputs">
      <Label>{t('inputs.label')}</Label>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {chips.length > 0 ? chips.map((path) => (
          <Badge key={path} variant="secondary" className="gap-1 pr-1 font-mono text-xs" data-testid={`derivation-input-${path}`}>
            {path}
            {!readOnly && (
              <button
                type="button"
                className="rounded-sm hover:bg-muted p-0.5"
                aria-label={t('inputs.remove', { path })}
                title={t('inputs.remove', { path })}
                onClick={() => onChange(value.filter((v) => v !== path))}
                data-testid={`derivation-input-remove-${path}`}
              >
                <AppIcon path={mdiClose} size="xs" />
              </button>
            )}
          </Badge>
        )) : (
          <span className="text-sm text-muted-foreground">{t('inputs.none')}</span>
        )}
      </div>
      {!readOnly && (
        <div className="flex flex-wrap items-end gap-2 mt-2">
          <div className="min-w-[220px] flex-1">
            <ApiSearchableSelect
              value={picked.uid}
              placeholder={t('inputs.property_placeholder')}
              onSearch={searchPropertyDefinitions}
              initialOptions={initialOptions}
              onOptionSelect={(option: ApiSearchableSelectOption) => setPicked({ uid: option.value, code: (option.data?.code as string) || '' })}
              data-testid="derivation-input-property"
            />
          </div>
          <Select value={ancestorType} onValueChange={setAncestorType}>
            <SelectTrigger className="w-[220px]" data-testid="derivation-input-ancestor">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SELF}>{t('inputs.on_asset')}</SelectItem>
              {assetTypes.map((at: any) => (
                <SelectItem key={at.uid} value={at.code}>
                  <span className="flex items-center gap-2">
                    <AssetTypeTile type={at as AssetTypeLike} size="sm" />
                    <span>{t('inputs.on_ancestor', { type: typeof at.name === 'string' ? at.name : at.name?.en || at.name?.fr || at.code })}</span>
                  </span>
                </SelectItem>
              ))}
              {assetTypes.map((at: any) => (
                <SelectItem key={`children-${at.uid}`} value={CHILDREN + at.code} data-testid={`derivation-input-children-${at.code}`}>
                  <span className="flex items-center gap-2">
                    <AssetTypeTile type={at as AssetTypeLike} size="sm" />
                    <span>{t('inputs.on_children', { type: typeof at.name === 'string' ? at.name : at.name?.en || at.name?.fr || at.code })}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" onClick={add} disabled={!pickedCode} data-testid="derivation-input-add">
            <AppIcon path={mdiPlus} size="sm" className="mr-1" />
            {t('inputs.add')}
          </Button>
        </div>
      )}
      <div className="flex items-center gap-2 mt-3">
        <Checkbox
          id="derivation-input-today"
          checked={readsToday}
          disabled={readOnly}
          onCheckedChange={(checked) => setToday(checked === true)}
          data-testid="derivation-input-today"
        />
        <Label htmlFor="derivation-input-today" className="text-sm font-normal">{t('inputs.today')}</Label>
      </div>
      <p className="text-xs text-muted-foreground mt-1">{t('inputs.help')}</p>
    </div>
  )
}
